// ============================================================================
// Button loading helper
// Wraps an async operation behind a single button. Applies the .btn--loading
// modifier (spinner + disabled + pointer-events none) while the promise is
// pending, shows a success/error toast, and restores the button on completion.
// ============================================================================

import { showToast } from 'shared/toast/toast.js';

/**
 * Run an async operation behind a button's loading state.
 *
 * @param {HTMLElement} btn - The button element (or any element with classList + disabled)
 * @param {() => Promise<any>} fn - Async function to run
 * @param {Object} [opts]
 * @param {string} [opts.successMessage] - Toast message on resolve (omit for silent success)
 * @param {string} [opts.errorMessage]   - Toast prefix on reject. Defaults to 'Something went wrong'.
 *                                         Actual error message is appended when available.
 * @param {boolean} [opts.rethrow=false] - Rethrow the error after showing the toast
 * @returns {Promise<any>} Resolves to the fn's return value, or undefined on error (unless rethrow)
 */
export async function withButtonLoading(btn, fn, opts = {}) {
    if (!btn) return fn();

    const wasDisabled = btn.disabled;
    btn.classList.add('btn--loading');
    btn.setAttribute('aria-busy', 'true');
    if ('disabled' in btn) btn.disabled = true;

    try {
        const result = await fn();
        if (opts.successMessage) showToast(opts.successMessage, 'success');
        return result;
    } catch (err) {
        console.error('[withButtonLoading]', err);
        const prefix = opts.errorMessage || 'Something went wrong';
        const detail = err?.message ? `: ${err.message}` : '';
        showToast(`${prefix}${detail}`, 'error');
        if (opts.rethrow) throw err;
        return undefined;
    } finally {
        if (btn.isConnected) {
            btn.classList.remove('btn--loading');
            btn.removeAttribute('aria-busy');
            if ('disabled' in btn) btn.disabled = wasDisabled;
        }
    }
}
